const baseDate = '2026-05-26T09:00:00.000Z';

// Recommendations match the real backend shape: plain { text }. The old
// safety_index/safety/match_pct fields were fabricated demo numbers that fed
// the removed "Recommended Claims" card — no backend field computes them.
function recommendation(text) {
  return { text };
}

function commonReport(bucket, overrides) {
  const upper = bucket.toUpperCase();
  return {
    id: `_preview-${bucket}`,
    short_id: `VP-PREVIEW-${upper}`,
    title: overrides.title,
    product_name: overrides.title,
    input_text: overrides.input_text,
    original_input: overrides.input_text,
    created_at: baseDate,
    credit_cost: 1,
    status: 'completed',
    _preview: true,
    analysis_result: {
      classification: overrides.classification || 'COSMETIC',
      risk_level: upper === 'LOW' ? 'LOW' : upper,
      confidence: overrides.confidence,
      explanation: overrides.explanation,
      extracted_claims: [overrides.input_text],
      rule_hits: overrides.rule_hits || [],
      recommendations: overrides.recommendations || [],
      optional_improvements: overrides.optional_improvements || [],
      questionnaire_answers: overrides.questionnaire_answers || {},
      citations: overrides.citations || [],
      // Real AnalysisResult.similar_cases shape (schemas.py) — a flat list of
      // rows carrying their own `type`, not an object keyed by drug/cosmetic
      // (ReportDetailPage buckets by `type`; see T5.3).
      similar_cases: overrides.similar_cases || [],
      scoring_breakdown: overrides.scoring_breakdown || { rule_engine: 0.9, vector: 0.82 },
      agent_metadata: {
        regulatory_basis: overrides.regulatory_basis,
        risk_assessment: overrides.risk_assessment,
        citations: overrides.citations || [],
        recommendations: (overrides.recommendations || []).map((item) => item.text || item),
      },
    },
  };
}

export function getPreviewReport(bucket) {
  switch (bucket) {
    case 'high':
      return commonReport('high', {
        title: 'Advanced Renewal Serum',
        input_text: 'Restores collagen, repairs damaged skin, and treats deep wrinkles in 7 days.',
        classification: 'DRUG',
        confidence: 0.94,
        explanation: 'This claim uses structure/function and treatment language that implies drug-level physiological effects.',
        regulatory_basis: 'Claims that a cosmetic product treats, repairs, or changes body structure can trigger drug classification under FD&C Act section 201(g)(1).',
        risk_assessment: 'High regulatory risk. The claim should be edited before publication and reviewed before paid media use.',
        rule_hits: [
          {
            rule_name: 'Drug structure/function indicator',
            classification: 'DRUG',
            severity: 'HIGH',
            risk_level: 'HIGH',
            citation: 'FD&C Act section 201(g)(1)',
            matched_terms: ['Restores collagen', 'repairs damaged skin', 'treats deep wrinkles'],
            suggested_alternative: 'Helps skin look smoother and visibly improves the appearance of fine lines.',
            rationale: 'Appearance-focused wording avoids implying physiological repair or disease treatment.',
          },
        ],
        recommendations: [
          recommendation('Helps skin look smoother and visibly improves the appearance of fine lines.'),
          recommendation('Supports a smoother-looking complexion with continued use.'),
        ],
        questionnaire_answers: { has_clinical_evidence: false },
        citations: [{ code: 'FD&C Act 201(g)(1)', text: 'Drug definition based on intended use claims.' }],
        similar_cases: [
          { type: 'drug_precedent', similarity: 0.91 },
          { type: 'drug_precedent', similarity: 0.84 },
          { type: 'cosmetic_safe', similarity: 0.31 },
        ],
        scoring_breakdown: { rule_engine: 0.96, vector: 0.88 },
      });
    case 'medium':
      return commonReport('medium', {
        title: 'Daily Clarity Moisturizer',
        input_text: 'Helps reduce signs of aging and supports a clearer-looking complexion.',
        classification: 'COSMETIC',
        confidence: 0.78,
        explanation: 'The claim is mostly appearance-focused, but anti-aging wording should be reviewed for context and substantiation.',
        regulatory_basis: 'Cosmetic appearance claims are generally lower risk, but ambiguous anti-aging language can require careful substantiation.',
        risk_assessment: 'Medium regulatory risk. Review the surrounding product context and keep the claim appearance-based.',
        rule_hits: [
          {
            rule_name: 'Ambiguous anti-aging language',
            classification: 'OTC',
            severity: 'MEDIUM',
            risk_level: 'MEDIUM',
            citation: 'FTC advertising substantiation policy',
            matched_terms: ['reduce signs of aging'],
            suggested_alternative: 'Helps skin look smoother and more refreshed.',
            rationale: 'Keeps the claim tied to visible appearance rather than biological aging.',
          },
        ],
        recommendations: [
          recommendation('Helps skin look smoother and more refreshed.'),
          recommendation('Supports a clearer-looking complexion with daily use.'),
        ],
        questionnaire_answers: { has_clinical_evidence: false },
        citations: [{ code: 'FTC Policy Statement', text: 'Advertising claims require a reasonable basis.' }],
        similar_cases: [
          { type: 'drug_precedent', similarity: 0.48 },
          { type: 'cosmetic_safe', similarity: 0.82 },
          { type: 'cosmetic_safe', similarity: 0.76 },
        ],
        scoring_breakdown: { rule_engine: 0.74, vector: 0.69 },
      });
    case 'low':
      return commonReport('low', {
        title: 'Hydra Calm Cream',
        input_text: 'Visibly improves the appearance of fine lines and helps skin look hydrated.',
        classification: 'COSMETIC',
        confidence: 0.998,
        explanation: 'This claim is appearance-focused and stays within cosmetic claim boundaries.',
        regulatory_basis: 'The wording focuses on visible appearance and does not imply disease treatment or physiological structure/function change.',
        risk_assessment: 'Low regulatory risk. The claim is ready for use, assuming ordinary substantiation is maintained.',
        rule_hits: [
          {
            rule_name: 'Cosmetic-safe appearance anchor',
            classification: 'COSMETIC',
            severity: 'LOW',
            risk_level: 'LOW',
            citation: 'Cosmetic appearance claim guidance',
            matched_terms: ['Visibly', 'appearance of'],
            suggested_alternative: 'Visibly improves the look of fine lines and helps skin appear hydrated.',
            rationale: 'Appearance anchors keep the claim in cosmetic territory.',
          },
        ],
        recommendations: [
          recommendation('Visibly improves the look of fine lines and helps skin appear hydrated.'),
          recommendation('Helps skin look smooth, fresh, and hydrated.'),
        ],
        optional_improvements: [
          { text: 'Visibly improves the look of fine lines and helps skin appear hydrated.' },
          { text: 'Helps skin look smooth, fresh, and hydrated.' },
        ],
        questionnaire_answers: { has_clinical_evidence: true },
        citations: [{ code: 'Cosmetic appearance claim guidance', text: 'Appearance-focused claims remain lower risk when properly substantiated.' }],
        similar_cases: [
          { type: 'drug_precedent', similarity: 0.08 },
          { type: 'cosmetic_safe', similarity: 0.94 },
          { type: 'cosmetic_safe', similarity: 0.91 },
        ],
        scoring_breakdown: { rule_engine: 1, vector: 0.96 },
      });
    default:
      return null;
  }
}
